import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { DashboardData } from '@/types';
import type { ActivityFeedItem } from '@/types/learner';
import { assignmentApi } from '@/services/assignmentApi';
import { learnerCourseApi, type EnrolledLiveWebinar } from '@/services/learnerCourseApi';
import { learnerApi } from '@/services/learnerApi';

export const useDashboardStore = defineStore('dashboard', () => {
  const data = ref<DashboardData | null>(null);
  const enrolledWebinars = ref<EnrolledLiveWebinar[]>([]);
  const recentActivity = ref<ActivityFeedItem[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const overdueAssignments = computed(() => data.value?.overdueAssignments ?? []);
  const inProgressCourses = computed(() => data.value?.inProgressCourses ?? []);
  const upcomingLiveCourses = computed(() => data.value?.upcomingLiveCourses ?? []);
  const newOnDemandCourses = computed(() => data.value?.newOnDemandCourses ?? []);

  async function fetchDashboard() {
    isLoading.value = true;
    error.value = null;
    try {
      const [assignments, inProgress, upcomingLive, newCourses, enrolled, history] =
        await Promise.all([
          assignmentApi.getOverdue().catch(() => []),
          learnerCourseApi.getInProgress().catch(() => []),
          learnerCourseApi.getUpcomingLive().catch(() => []),
          learnerCourseApi.getNew().catch(() => []),
          learnerCourseApi.getEnrolledLive().catch(() => []),
          learnerApi.getActivity({ limit: 5 }).catch(() => ({ items: [] })),
        ]);

      data.value = {
        user: { firstName: 'Jane', lastName: 'Doe' }, // User profile from userStore
        overdueAssignments: assignments,
        inProgressCourses: inProgress,
        upcomingLiveCourses: upcomingLive,
        newOnDemandCourses: newCourses,
        recentAssignments: [],
        stats: {
          totalAssignments: assignments.length,
          overdueCount: assignments.length,
          completedThisMonth: inProgress.filter((c) => c.status === 'completed').length,
          creditsEarnedThisMonth: inProgress
            .filter((c) => c.status === 'completed')
            .reduce((sum, c) => sum + (c.credits?.[0]?.amount ?? 0), 0),
        },
      };
      enrolledWebinars.value = enrolled;
      recentActivity.value = history.items;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch dashboard';
    } finally {
      isLoading.value = false;
    }
  }

  return {
    data,
    enrolledWebinars,
    recentActivity,
    isLoading,
    error,
    overdueAssignments,
    inProgressCourses,
    upcomingLiveCourses,
    newOnDemandCourses,
    fetchDashboard,
  };
});
